import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CrmCallQueue } from './CrmCallQueue'
import { CrmAttentionDock } from './CrmAttentionDock'
import { getMySchedule, ScheduleFeed } from './crmApi'
import { useAuth } from '../../contexts/AuthContext'
import { useTodos, type ListKey } from '../todo/useTodos'
import { useSearchParams } from 'react-router-dom'

/**
 * MY DAY (Lee's notebook, 26 Oct 2026): the morning cockpit. The CALL QUEUE
 * sits at the heart of it (left, the working pane); the right pane carries
 * everything else the day needs as tabs — TODAY (your calls + meetings +
 * demos from the schedule feed), NEEDS ATTENTION (the attention panel) and
 * TO-DOS (the journey task board). Replaces the standalone Call Queue and
 * Task Dashboard pages.
 */

const fmtT = (iso?: string | null) => (iso ? new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '—')

const TodayPanel: React.FC = () => {
  const navigate = useNavigate()
  const [feed, setFeed] = useState<ScheduleFeed | null>(null)
  const load = () => {
    // The /crm/bookings/mine route validates from/to as plain YYYY-MM-DD (local
    // day) — full ISO datetimes fail validation and the panel showed an empty
    // day while the Calls dock (correct format) showed the real diary
    // (Lee, 27 Oct 2026).
    const d = new Date()
    const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    getMySchedule(day, day).then(setFeed).catch(() => setFeed({ bookings: [], google: { available: false, events: [] }, demos: [] } as any))
  }
  useEffect(() => { load(); const t = setInterval(load, 120_000); return () => clearInterval(t) }, [])
  if (!feed) return <p className="p-3 text-[12px] text-txt-tertiary">Loading today…</p>

  type Item = { at: string | null; kind: string; title: string; sub?: string; url?: string; dealId?: string | null; done?: boolean }
  const items: Item[] = [
    ...feed.bookings.map((b: any) => ({
      at: b.startAt, kind: b.type === 'call' ? 'CALL' : 'MEETING', title: b.title,
      sub: b.phone || undefined, url: b.meetUrl || undefined, dealId: b.dealId, done: b.status === 'done',
    })),
    ...feed.demos.map((d: any) => ({ at: d.startAt, kind: 'DEMO', title: d.title || d.eventName || 'Demo', sub: d.inviteeName || d.inviteeEmail || undefined, url: d.meetUrl || undefined, dealId: d.dealId })),
    ...(feed.google?.events || []).map((g: any) => ({ at: g.startAt || g.start, kind: 'CALENDAR', title: g.title || g.summary || 'Event', sub: g.dealName || undefined, dealId: g.dealId || null, url: g.meetUrl || g.hangoutLink || undefined })),
  ]
    // The Google range read pads ±6h around the day — trim to the local day so
    // yesterday-evening events never open the list.
    .filter((it) => {
      if (!it.at) return false
      const t = new Date(it.at); const n = new Date()
      return t.getFullYear() === n.getFullYear() && t.getMonth() === n.getMonth() && t.getDate() === n.getDate()
    })
    .sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')))

  const KIND: Record<string, string> = { CALL: '#10b981', MEETING: '#2563eb', DEMO: '#7c3aed', CALENDAR: '#64748b' }
  return (
    <div className="p-2.5 space-y-1.5">
      {items.length === 0 && <p className="text-[12px] text-txt-tertiary px-1 py-2">Nothing scheduled today. The queue awaits.</p>}
      {items.map((it, i) => (
        <div key={i} className={`rounded-lg border border-border bg-surface px-2.5 py-2 flex items-center gap-2.5 ${it.done ? 'opacity-55' : ''}`}>
          <span className="text-[12px] font-bold tabular-nums text-txt-primary w-11 shrink-0">{fmtT(it.at)}</span>
          <span className="px-1.5 py-0.5 rounded text-[8.5px] font-bold text-white shrink-0" style={{ background: KIND[it.kind] || '#64748b' }}>{it.kind}</span>
          <span className="min-w-0 flex-1">
            <span className={`block text-[12px] font-semibold truncate ${it.done ? 'line-through text-txt-tertiary' : 'text-txt-primary'} ${it.dealId ? 'cursor-pointer hover:text-brand-600' : ''}`}
              onClick={() => it.dealId && navigate(`/crm/lead/${it.dealId}`)}>{it.title}</span>
            {it.sub && <span className="block text-[10px] text-txt-tertiary truncate">{it.sub}</span>}
          </span>
          {it.url && !it.done && (
            <a href={it.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
              className="px-2 py-1 rounded text-[10px] font-bold bg-brand-600 text-white hover:bg-brand-700 shrink-0">Join</a>
          )}
        </div>
      ))}
    </div>
  )
}

/** PERSONAL TO-DOS (Lee, 26 Oct 2026): the SAME lists as the TO-DO notebook
 *  dock (shared useTodos store), so the tab and the dock always correspond —
 *  Today · Tomorrow · This week · Pending, add/tick/delete. */
const LIST_TABS: Array<{ key: ListKey; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'week', label: 'This week' },
  { key: 'pending', label: 'Pending' },
]
const TodosPanel: React.FC = () => {
  const { currentAgent } = useAuth()
  const t = useTodos(String(currentAgent?.id || ''))
  const [list, setList] = useState<ListKey>('today')
  const [text, setText] = useState('')
  if (!currentAgent) return null
  const items = t.todos[list] || []
  const add = () => { const v = text.trim(); if (!v) return; t.addTask(list, v); setText('') }
  return (
    <div className="p-2.5">
      <div className="flex items-center gap-1 flex-wrap">
        {LIST_TABS.map((lt) => (
          <button key={lt.key} onClick={() => setList(lt.key)}
            className={`px-2 py-1 rounded-full text-[10.5px] font-bold border ${list === lt.key ? 'bg-txt-primary text-surface border-txt-primary' : 'bg-surface text-txt-secondary border-border hover:bg-surface-hover'}`}>
            {lt.label} <span className="opacity-60">({(t.todos[lt.key] || []).length})</span>
          </button>
        ))}
      </div>
      {list !== 'pending' && (
        <div className="flex gap-1.5 mt-2.5">
          <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()}
            placeholder={`Add to ${LIST_TABS.find((x) => x.key === list)?.label.toLowerCase()}… (Enter)`}
            className="flex-1 px-2.5 py-2 rounded-lg border border-border bg-surface text-[12.5px] text-txt-primary outline-none focus:border-brand-500" />
          <button onClick={add} disabled={!text.trim()}
            className="px-3 rounded-lg text-[12px] font-bold bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40">Add</button>
        </div>
      )}
      <div className="mt-2.5 space-y-1.5">
        {items.length === 0 && <p className="text-[12px] text-txt-tertiary px-1 py-2">Nothing in this list.</p>}
        {items.map((td) => (
          <div key={td.id} className="group rounded-lg border border-border bg-surface px-2.5 py-2">
            <div className="flex items-center gap-2.5">
              <input type="checkbox" checked={td.done} className="accent-brand-600 w-4 h-4 shrink-0 cursor-pointer" onChange={() => t.toggleTask(td.id)} />
              <span className={`flex-1 min-w-0 text-[12.5px] leading-snug [overflow-wrap:anywhere] ${td.done ? 'line-through text-txt-tertiary' : 'text-txt-primary'}`}>{td.text}</span>
              {td.from && <span className="text-[9px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 shrink-0" title={`Rolled over from ${td.from}`}>FROM {td.from.split(' ').slice(0, 3).join(' ').toUpperCase()}</span>}
              <button onClick={() => t.removeTask(td.id)}
                className="opacity-0 group-hover:opacity-100 text-txt-tertiary hover:text-red-600 text-[14px] leading-none shrink-0" title="Delete">×</button>
            </div>
            {td.subtasks.length > 0 && (
              <div className="mt-1 ml-6 space-y-0.5">
                {td.subtasks.map((st) => (
                  <label key={st.id} className="flex items-center gap-2 text-[11.5px]">
                    <input type="checkbox" checked={st.done} className="accent-brand-600 w-3 h-3" onChange={() => t.toggleSubtask(td.id, st.id)} />
                    <span className={st.done ? 'line-through text-txt-tertiary' : 'text-txt-secondary'}>{st.text}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const RIGHT_TABS = [
  { key: 'today', label: 'Today' },
  { key: 'attention', label: 'Needs attention' },
  { key: 'todos', label: 'To-dos' },
]

export const CrmMyDay: React.FC = () => {
  const [sp] = useSearchParams()
  const [tab, setTab] = useState<string>(() => {
    const fromUrl = sp.get('tab')
    if (fromUrl && ['today', 'attention', 'todos'].includes(fromUrl)) return fromUrl
    try { return localStorage.getItem('wq_myday_tab') || 'today' } catch { return 'today' }
  })
  useEffect(() => { try { localStorage.setItem('wq_myday_tab', tab) } catch { /* ignore */ } }, [tab])

  return (
    <div className="h-full flex overflow-hidden bg-surface-secondary">
      {/* THE HEART: the call queue, full working pane. */}
      <div className="flex-1 min-w-0 border-r border-border overflow-hidden">
        <CrmCallQueue />
      </div>
      {/* The day's context: today's schedule · attention · to-dos. */}
      <div className="w-[420px] max-w-[45%] shrink-0 flex flex-col overflow-hidden bg-surface">
        <div className="shrink-0 flex items-center gap-1 px-3 pt-2.5 border-b border-border">
          {RIGHT_TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-2.5 py-1.5 rounded-t-lg text-[11.5px] font-bold border-b-2 -mb-px ${tab === t.key ? 'text-brand-700 border-brand-600' : 'text-txt-tertiary border-transparent hover:text-txt-primary'}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div key={tab} className="flex-1 min-h-0 overflow-y-auto animate-fade-in">
          {tab === 'today' && <TodayPanel />}
          {tab === 'attention' && <CrmAttentionDock embedded />}
          {tab === 'todos' && <TodosPanel />}
        </div>
      </div>
    </div>
  )
}

export default CrmMyDay
